import { c as _c } from "react/compiler-runtime";
import posthog from 'posthog-js';
import { useContext, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router';
import styled, { css } from 'styled-components';
import getThemes from '@hookdeck/theme/src/themes';
import Icon from '../common/base/Icon';
import Link from '../common/base/Link';
import Text from '../common/base/Text';
import { DialogProvider } from '../common/Dialog';
import { Div } from '../common/helpers/StyledUtils';
import ViewRoute from '../common/ViewRoute';
import { TeamPermission } from '../contexts/TeamPermissionContext';
import useScrollToTop from '../hooks/useScrollToTop';
import EventGatewayScene from './DashboardScene';
import CliAuthView from './DashboardScene/CliAuth';
import ConnectView from './DashboardScene/Connect';
import { DashboardContext, DashboardContextProvider, WithTeamOnly, WithUserOnly } from './DashboardScene/DashboardContext';
import { RefreshKeyProvider } from './DashboardScene/Metrics/RefreshKeyContext';
import OnboardingView from './DashboardScene/Onboarding';
import { QuotaEnforcementBanner } from './DashboardScene/QuotaEnforcementBanner';
import { redirects } from './DashboardScene/redirects';
import TeamSelect from './DashboardScene/TeamSelect';
import OutpostScene from './OutpostScene';
import GuidedCreateConnection from './DashboardScene/GuidedConnection/GuidedConnection';
import GuidedCLIOnboarding from './DashboardScene/GuidedCLIOnboarding/GuidedCLIOnboarding';
const StyledDegradedServiceBanner = styled.div(({
  theme: current_theme
}) => {
  const themes = getThemes();
  const theme = current_theme.mode === 'light' ? themes.dark : themes.light;
  return css`
    background-color: ${theme.colors.surface.base.background};
    color: ${theme.colors.on.neutral.primary_neutral};
    height: 32px;
    position: sticky;
    top: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${({
    theme
  }) => theme.spacing(4)};
    z-index: ${theme.zindex.fixed};

    a,
    button {
      color: ${theme.colors.on.neutral.primary_neutral};
      text-wrap: nowrap;

      span {
        color: inherit !important;
      }

      &:hover,
      & > * {
        background-color: inherit;
        cursor: pointer;
      }
    }

    & > div:first-child,
    & > div:last-child {
      width: 150px;
    }

    @media (max-width: 835px) {
      .outage-description {
        display: none;
      }
    }
  `;
});
function DegradedServiceBanner() {
  const $ = _c(7);
  const {
    is_degraded_banner_active,
    dismissDegradedBanner
  } = useContext(DashboardContext);
  if (!is_degraded_banner_active) {
    return null;
  }
  let t0;
  if ($[0] !== dismissDegradedBanner) {
    t0 = () => {
      dismissDegradedBanner();
    };
    $[0] = dismissDegradedBanner;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleBannerClose = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Div flex={{
      gap: 1,
      align: "center"
    }}><Icon icon="info" /><Text bold={true} text_wrap={false}>Service degradation</Text></Div>;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Div flex={{
      gap: 1,
      align: "center",
      justify: "center"
    }}><span className="outage-description">Hookdeck services are currently degraded.</span><Link href="https://status.hookdeck.com/" target="_blank" icon="link">Follow updates on our status page</Link></Div>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      gap: 1,
      align: "center",
      justify: "flex-end"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== handleBannerClose) {
    t4 = <StyledDegradedServiceBanner>{t1}{t2}<Div flex={t3}><Link icon="close" small={true} onClick={handleBannerClose} /></Div></StyledDegradedServiceBanner>;
    $[5] = handleBannerClose;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
const handleRouteChange = () => {
  posthog?.capture('$pageview');
};
const DashboardScene: React.FC = () => {
  const $ = _c(15);
  const location = useLocation();
  const {
    team,
    subscription,
    is_degraded_banner_active
  } = useContext(DashboardContext);
  const [current_route, setCurrentRoute] = useState(null);
  const is_outpost_type = team?.type === "outpost";
  const is_quota_banner_active = Boolean(!is_outpost_type && (subscription?.limit_exceeded_at || subscription?.quota_enforcement_at));
  const banner_active = is_degraded_banner_active || is_quota_banner_active;
  let t0;
  let t1;
  if ($[0] !== current_route || $[1] !== location.pathname) {
    t0 = () => {
      if (current_route === location.pathname) {
        return;
      }
      handleRouteChange();
      setCurrentRoute(location.pathname);
    };
    t1 = [location.pathname, current_route];
    $[0] = current_route;
    $[1] = location.pathname;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  useScrollToTop();
  let t2;
  let t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ViewRoute usePathAsTitle={true} capitalizeSubtitle={true} path="/onboarding" component={OnboardingView} />;
    t3 = <ViewRoute usePathAsTitle={true} path="/cli-auth/:key" component={CliAuthView} />;
    t4 = <ViewRoute title="Select Project" usePathAsTitle={true} path="/projects" component={TeamSelect} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ViewRoute title="Create connection" usePathAsTitle={true} path="/create-first-connection" component={GuidedCreateConnection} />;
    t6 = <ViewRoute title="Create CLI connection" usePathAsTitle={true} path="/create-first-cli-connection" component={GuidedCLIOnboarding} />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Route path={["/connect/:source_id", "/connect"]} component={ConnectView} />;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DegradedServiceBanner />;
    t9 = <QuotaEnforcementBanner />;
    $[10] = t8;
    $[11] = t9;
  } else {
    t8 = $[10];
    t9 = $[11];
  }
  let t10;
  if ($[12] !== banner_active || $[13] !== is_outpost_type) {
    t10 = <Switch>{t2}{t3}{t4}<Route><WithTeamOnly><TeamPermission role="viewer"><Switch>{t5}{t6}{t7}<Route>{t8}{t9}<RefreshKeyProvider>{is_outpost_type ? <OutpostScene banner_active={banner_active} /> : <EventGatewayScene banner_active={banner_active} />}</RefreshKeyProvider></Route></Switch></TeamPermission></WithTeamOnly></Route></Switch>;
    $[12] = banner_active;
    $[13] = is_outpost_type;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  return t10;
};
export default () => {
  const location = useLocation();
  return <WithUserOnly>
      <Switch>
        {redirects.map(redirect => <Redirect key={redirect.from} from={redirect.from} to={{
        ...location,
        pathname: redirect.to
      }} exact={redirect.exact} />)}
      </Switch>
      <DashboardContextProvider>
        <DialogProvider>
          <DashboardScene />
        </DialogProvider>
      </DashboardContextProvider>
    </WithUserOnly>;
};